import { FC, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CAMINHO,
  CONFERENCIAS,
  DIFERENCAS_PRODUZIDO,
  FILTRO_PAINEIS,
  GLOSSARIO,
  JORNADA_DO_LOTE,
  LEITURA_DO_HORARIO,
  ORIGEM_PAINEIS,
  type EtapaCaminho,
  type NumeroPainel,
  type OrigemPainel,
} from '@/config/origemDados';

const CARTAO = 'rounded-xl border border-theme-border bg-surface-card';

const Icone: FC<{ nome: string; className?: string }> = ({ nome, className = '' }) => (
  <span aria-hidden className={`material-symbols-outlined leading-none ${className}`}>
    {nome}
  </span>
);

const TituloSecao: FC<{ children: string; apoio?: string }> = ({ children, apoio }) => (
  <div>
    <h2 className="text-lg font-semibold text-content-primary">{children}</h2>
    {apoio && <p className="mt-0.5 max-w-3xl text-sm text-content-secondary">{apoio}</p>}
  </div>
);

/** Uma etapa do caminho; a seta aponta para a próxima (para baixo no celular, para o lado no desktop). */
const Etapa: FC<{ etapa: EtapaCaminho; n: number; ultima: boolean }> = ({ etapa, n, ultima }) => (
  <li className="relative flex flex-col" data-testid={`caminho-${etapa.id}`}>
    <div className={`${CARTAO} flex h-full flex-col gap-2 p-4`}>
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-contrast">
          {n}
        </span>
        <Icone nome={etapa.icone} className="text-[20px] text-accent" />
        <h3 className="font-semibold text-content-primary">{etapa.titulo}</h3>
      </div>
      <p className="text-sm text-content-secondary">{etapa.oQueAcontece}</p>
      <p className="mt-auto flex items-start gap-1.5 text-xs text-content-muted">
        <Icone nome="schedule" className="text-[16px]" />
        {etapa.quando}
      </p>
    </div>
    {!ultima && (
      <>
        <Icone nome="arrow_downward" className="mx-auto my-1 text-[20px] text-content-muted lg:hidden" />
        <Icone
          nome="arrow_forward"
          className="absolute -right-5 top-1/2 hidden -translate-y-1/2 text-[20px] text-content-muted lg:block"
        />
      </>
    )}
  </li>
);

const LinhaNumero: FC<{ numero: NumeroPainel }> = ({ numero }) => (
  <li className={`${CARTAO} flex flex-col gap-2 p-4 text-sm`}>
    <h4 className="font-semibold text-content-primary">{numero.nome}</h4>
    <dl className="grid gap-2">
      <div>
        <dt className="text-xs uppercase tracking-wide text-content-muted">De onde vem</dt>
        <dd className="text-content-secondary">{numero.deOnde}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide text-content-muted">Como calculamos</dt>
        <dd className="text-content-secondary">{numero.comoCalculamos}</dd>
      </div>
      {numero.noDatasul && (
        <div>
          <dt className="text-xs uppercase tracking-wide text-content-muted">No Datasul</dt>
          <dd className="text-content-secondary">{numero.noDatasul}</dd>
        </div>
      )}
    </dl>
  </li>
);

const SecaoPainel: FC<{ painel: OrigemPainel }> = ({ painel }) => (
  <section id={painel.chave} className="scroll-mt-6" data-testid={`origem-painel-${painel.chave}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-semibold text-content-primary">{painel.titulo}</h3>
      <Link to={painel.rota} className="text-sm font-medium text-accent hover:underline">
        Abrir o painel →
      </Link>
    </div>
    <p className="mt-0.5 max-w-3xl text-sm text-content-secondary">{painel.resumo}</p>
    <ul className="mt-3 grid gap-3 md:grid-cols-2">
      {painel.numeros.map((numero) => (
        <LinhaNumero key={numero.nome} numero={numero} />
      ))}
    </ul>
  </section>
);

const SecaoProduzido: FC = () => (
  <section id="produzido" className="scroll-mt-6 flex flex-col gap-4" data-testid="origem-produzido">
    <TituloSecao apoio="Desde 14/10/2026 os painéis contam a produção como o CP0511 do Datasul: a quantidade reportada nas ordens de produção, pela data de início da OP, sem as OPs de reprocesso. É a mesma régua em todas as abas, combinada com PCP, produção e TI. Antes a régua era o pallet montado (KECP211, de 29/09 a 14/10/2026) e, até 29/09, o lote entrando no CQ.">
      Produzido: o reportado nas OPs
    </TituloSecao>
    <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {JORNADA_DO_LOTE.map((etapa, i) => (
        <li
          key={etapa.titulo}
          className={`flex flex-col gap-1 rounded-xl border bg-surface-card p-3 text-sm ${
            etapa.marca ? 'border-accent' : 'border-theme-border'
          }`}
        >
          <span className="text-xs text-content-muted">{i + 1}</span>
          <span className="font-semibold text-content-primary">{etapa.titulo}</span>
          <span className="text-content-secondary">{etapa.detalhe}</span>
          {etapa.marca && (
            <span className="mt-1 inline-flex w-fit items-center gap-1 rounded bg-surface-tertiary px-1.5 py-0.5 text-xs font-medium text-accent">
              <Icone nome="straighten" className="text-[14px]" />
              {etapa.marca}
            </span>
          )}
        </li>
      ))}
    </ol>
    <div className={`${CARTAO} p-4 text-sm`}>
      <p className="font-semibold text-content-primary">Onde o painel e o CP0511 ainda podem mostrar números diferentes</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-content-secondary">
        {DIFERENCAS_PRODUZIDO.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>
    </div>
  </section>
);

/**
 * "De onde vêm os dados" (`/relatorios/origem-dos-dados`).
 *
 * Página para quem usa os painéis e não precisa saber o que é uma réplica: o caminho
 * do dado do Datasul até a tela, a regra de cada número e as conferências feitas.
 * O card do topo de cada painel aponta para a seção dele (`#sop`, `#vendas`…).
 */
const OrigemDadosPage: FC = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <div className="flex flex-col gap-10 p-6 lg:p-10">
      <header>
        <p className="text-xs uppercase tracking-wide text-content-muted">Relatórios · Equiplex</p>
        <h1 className="text-2xl font-bold text-content-primary">De onde vêm os dados</h1>
        <p className="mt-1 max-w-3xl text-sm text-content-secondary">
          Todo número dos painéis sai do Datasul. Nenhuma planilha entra no caminho. Aqui está por onde o
          dado passa até chegar na tela e como cada número é calculado.
        </p>
        <p className="mt-1 max-w-3xl text-sm text-content-secondary">
          A única coisa digitada na plataforma são os apontamentos da reunião: explicam os desvios e não mudam
          número nenhum.
        </p>
      </header>

      <section className="flex flex-col gap-4" data-testid="origem-caminho">
        <TituloSecao>O caminho do dado</TituloSecao>
        <ol className="grid gap-x-10 lg:grid-cols-4">
          {CAMINHO.map((etapa, i) => (
            <Etapa key={etapa.id} etapa={etapa} n={i + 1} ultima={i === CAMINHO.length - 1} />
          ))}
        </ol>
        <div className={`${CARTAO} flex items-start gap-3 p-4 text-sm`}>
          <Icone nome="nightlight" className="text-[20px] text-accent" />
          <p className="text-content-secondary">{LEITURA_DO_HORARIO}</p>
        </div>
      </section>

      <section className="flex flex-col gap-6">
        <TituloSecao apoio="Para cada painel: de onde vem o número, como é calculado e com qual relatório do Datasul ele conversa.">
          Os números de cada painel
        </TituloSecao>
        {ORIGEM_PAINEIS.map((painel) => (
          <SecaoPainel key={painel.chave} painel={painel} />
        ))}
      </section>

      <section id="filtro" className="scroll-mt-6 flex flex-col gap-4" data-testid="origem-filtro">
        <TituloSecao apoio="Período, tipo de mês e produtos: a barra no topo de cada painel.">O filtro dos painéis</TituloSecao>
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {FILTRO_PAINEIS.map((item) => (
            <li key={item.titulo} className={`${CARTAO} flex flex-col gap-1 p-4 text-sm`}>
              <span className="flex items-center gap-1.5 font-semibold text-content-primary">
                <Icone nome="filter_alt" className="text-[18px] text-accent" />
                {item.titulo}
              </span>
              <span className="text-content-secondary">{item.texto}</span>
            </li>
          ))}
        </ul>
      </section>

      <SecaoProduzido />

      <section className="flex flex-col gap-4" data-testid="origem-conferencias">
        <TituloSecao apoio="O que já foi comparado com o Datasul, e quando.">Conferências</TituloSecao>
        <ul className="grid gap-3 md:grid-cols-3">
          {CONFERENCIAS.map((c) => (
            <li key={c.titulo} className={`${CARTAO} flex flex-col gap-1 p-4 text-sm`}>
              <span className="flex items-center gap-1.5 font-semibold text-content-primary">
                <Icone nome="task_alt" className="text-[18px] text-accent" />
                {c.titulo}
              </span>
              <span className="text-content-secondary">{c.resultado}</span>
              <span className="mt-auto pt-1 text-xs text-content-muted">Conferido em {c.data}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-4" data-testid="origem-glossario">
        <TituloSecao>Glossário</TituloSecao>
        <dl className="grid gap-x-8 gap-y-3 text-sm md:grid-cols-2">
          {GLOSSARIO.map((g) => (
            <div key={g.termo}>
              <dt className="font-semibold text-content-primary">{g.termo}</dt>
              <dd className="text-content-secondary">{g.significado}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
};

export default OrigemDadosPage;
